import React, {useContext, useRef, useEffect} from "react";
import classnames from "classnames";
import {
    Input,
    InputGroupAddon,
    InputGroupText,
    InputGroup,
    Row,
    Col,
    CardHeader,
    Card,
    CardBody,
    Button,
    FormGroup,
    Label
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import { UserLoginContext } from "../../context/UserLoginContext";
import {useHistory, useParams} from "react-router-dom";
import rest from "../../API/api";
import { authenticatedFetch } from "../../services/authService";

const EditWaiver = () => {
    const {id} = useParams();
    const [userState, setUserState] = useContext(UserLoginContext);
    const notificationAlertRef = useRef(null);
    const history = useHistory();
    const fileInputRef = useRef(null);

    const [loading, setLoading] = React.useState(true);

    // Basic fields
    const [title, setTitle] = React.useState("");
    const [titleState, setTitleState] = React.useState("has-success");
    const [description, setDescription] = React.useState("");

    // Header fields
    const [header1, setHeader1] = React.useState("");
    const [header2, setHeader2] = React.useState("");
    const [header3, setHeader3] = React.useState("");

    // Waiver text fields
    const [waiverText1, setWaiverText1] = React.useState("");
    const [waiverText2, setWaiverText2] = React.useState("");
    const [waiverText3, setWaiverText3] = React.useState("");

    // Image and settings
    const [imageUrl, setImageUrl] = React.useState("");
    const [imageFile, setImageFile] = React.useState(null);
    const [imagePreview, setImagePreview] = React.useState("");
    const [version, setVersion] = React.useState("1.0");
    const [enabled, setEnabled] = React.useState(true);

    // Focus states for fields with icons
    const [titleFocus, setTitleFocus] = React.useState(false);
    const [versionFocus, setVersionFocus] = React.useState(false);
    const [descriptionFocus, setDescriptionFocus] = React.useState(false);
    const [imageUrlFocus, setImageUrlFocus] = React.useState(false);

    useEffect(() => {
        if (userState.status && id) {
            loadWaiver();
        }
    }, [userState.status, id]);

    const loadWaiver = async () => {
        try {
            const response = await authenticatedFetch(`${rest.waivers}/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                }
            });

            if (response.ok) {
                const waiver = await response.json();
                setTitle(waiver.Title || "");
                setDescription(waiver.Description || "");
                setHeader1(waiver.Header1 || "");
                setHeader2(waiver.Header2 || "");
                setHeader3(waiver.Header3 || "");
                setWaiverText1(waiver.WaiverText1 || "");
                setWaiverText2(waiver.WaiverText2 || "");
                setWaiverText3(waiver.WaiverText3 || "");
                setImageUrl(waiver.ImageUrl || "");
                if (waiver.ImageUrl) {
                    setImagePreview(waiver.ImageUrl);
                }
                setVersion(waiver.Version || "1.0");
                setEnabled(waiver.Enabled !== false);
                setLoading(false);
            } else {
                notify("danger", "Failed to load waiver");
                history.push("/admin/waivers");
            }
        } catch (error) {
            console.error('Error loading waiver:', error);
            notify("danger", "Error loading waiver");
            history.push("/admin/waivers");
        }
    };

    const verifyLength = (value, length) => {
        return value.length >= length;
    };

    const notify = (type, message) => {
        const options = {
            place: "tc",
            message: <div>{message}</div>,
            type: type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 5,
        };
        notificationAlertRef.current.notificationAlert(options);
    };

    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setImageFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
                setImageUrl(reader.result); // Use base64 as URL
            };
            reader.readAsDataURL(file);
        }
    };

    const isValidated = () => {
        if (!verifyLength(title, 3)) {
            setTitleState("has-danger");
            notify("danger", "Please enter a title (min 3 characters)");
            return false;
        }

        // At least one header and one waiver text must be filled
        if (!header1.trim() && !header2.trim() && !header3.trim()) {
            notify("danger", "Please enter at least one header");
            return false;
        }

        if (!waiverText1.trim() && !waiverText2.trim() && !waiverText3.trim()) {
            notify("danger", "Please enter at least one waiver text section");
            return false;
        }

        setTitleState("has-success");
        return true;
    };

    const handleSubmit = async () => {
        if (isValidated()) {
            try {
                const waiver = {
                    Title: title,
                    Description: description,
                    Header1: header1,
                    Header2: header2,
                    Header3: header3,
                    WaiverText1: waiverText1,
                    WaiverText2: waiverText2,
                    WaiverText3: waiverText3,
                    ImageUrl: imageUrl,
                    Version: version,
                    Enabled: enabled
                };

                const response = await authenticatedFetch(`${rest.waivers}/${id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    },
                    body: JSON.stringify(waiver)
                });

                if (response.ok) {
                    notify("success", "Waiver updated successfully!");
                    setTimeout(() => {
                        history.push("/admin/waivers");
                    }, 1500);
                } else {
                    notify("danger", "Failed to update waiver");
                }
            } catch (error) {
                console.error('Error updating waiver:', error);
                notify("danger", "Error updating waiver");
            }
        }
    };

    const handleCancel = () => {
        history.push("/admin/waivers");
    };

    if (loading) {
        return (
            <div className="content">
                <Row>
                    <Col md="12">
                        <Card>
                            <CardBody>
                                <p>Loading waiver...</p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }

    if (userState.status) {
        return (
            <>
                <div className="rna-container">
                    <NotificationAlert ref={notificationAlertRef} />
                </div>
                <div className="content">
                    <style>
                        {`
                            .input-group {
                                border: 2px solid #2b3553 !important;
                                border-radius: 4px !important;
                                background-color: transparent !important;
                            }
                            .input-group .form-control {
                                border: none !important;
                                background-color: transparent !important;
                            }
                            .input-group .input-group-text {
                                border: none !important;
                                background-color: transparent !important;
                            }
                            .input-group:focus-within {
                                border-color: #1d8cf8 !important;
                                box-shadow: 0 0 0 0.2rem rgba(29, 140, 248, 0.25) !important;
                            }
                            .input-group.has-success {
                                border-color: #00bf9a !important;
                            }
                            .input-group.has-danger {
                                border-color: #fd5d93 !important;
                            }
                            /* Override checkbox color to blue */
                            .form-check-sign::before,
                            .form-check-sign::after {
                                background-color: #1d8cf8 !important;
                            }
                            input[type="checkbox"]:checked + .form-check-sign::before,
                            input[type="checkbox"]:checked + .form-check-sign::after {
                                background-color: #1d8cf8 !important;
                            }
                        `}
                    </style>
                    <Row>
                        <Col md="12">
                            <Card>
                                <CardHeader>
                                    <h5 className="title">Edit Waiver</h5>
                                    <p className="category">Update the waiver template</p>
                                </CardHeader>
                                <CardBody>
                                    <form>
                                        {/* Basic Information */}
                                        <Row>
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label>Waiver Title <span className="text-danger">*</span></Label>
                                                    <InputGroup className={classnames(titleState, {
                                                        "input-group-focus": titleFocus
                                                    })}>
                                                        <InputGroupAddon addonType="prepend">
                                                            <InputGroupText>
                                                                <i className="tim-icons icon-single-copy-04"/>
                                                            </InputGroupText>
                                                        </InputGroupAddon>
                                                        <Input
                                                            type="text"
                                                            value={title}
                                                            onChange={(e) => setTitle(e.target.value)}
                                                            onFocus={() => setTitleFocus(true)}
                                                            onBlur={() => setTitleFocus(false)}
                                                        />
                                                    </InputGroup>
                                                    {titleState === "has-danger" && (
                                                        <label className="error">
                                                            Please enter a title (min 3 characters).
                                                        </label>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label>Version</Label>
                                                    <InputGroup className={classnames({
                                                        "input-group-focus": versionFocus
                                                    })}>
                                                        <InputGroupAddon addonType="prepend">
                                                            <InputGroupText>
                                                                <i className="tim-icons icon-tag"/>
                                                            </InputGroupText>
                                                        </InputGroupAddon>
                                                        <Input
                                                            type="text"
                                                            value={version}
                                                            onChange={(e) => setVersion(e.target.value)}
                                                            onFocus={() => setVersionFocus(true)}
                                                            onBlur={() => setVersionFocus(false)}
                                                        />
                                                    </InputGroup>
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md="12">
                                                <FormGroup>
                                                    <Label>Description</Label>
                                                    <InputGroup className={classnames({
                                                        "input-group-focus": descriptionFocus
                                                    })}>
                                                        <InputGroupAddon addonType="prepend">
                                                            <InputGroupText>
                                                                <i className="tim-icons icon-align-left-2"/>
                                                            </InputGroupText>
                                                        </InputGroupAddon>
                                                        <Input
                                                            type="textarea"
                                                            rows="2"
                                                            value={description}
                                                            onChange={(e) => setDescription(e.target.value)}
                                                            onFocus={() => setDescriptionFocus(true)}
                                                            onBlur={() => setDescriptionFocus(false)}
                                                        />
                                                    </InputGroup>
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        {/* Section 1 */}
                                        <h5 className="title mt-4">Section 1</h5>
                                        <Row>
                                            <Col md="12">
                                                <FormGroup>
                                                    <Label>Header 1</Label>
                                                    <Input
                                                        type="text"
                                                        placeholder="e.g., Assumption of Risk"
                                                        value={header1}
                                                        onChange={(e) => setHeader1(e.target.value)}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="12">
                                                <FormGroup>
                                                    <Label>Waiver Text 1</Label>
                                                    <Input
                                                        type="textarea"
                                                        rows="5"
                                                        value={waiverText1}
                                                        onChange={(e) => setWaiverText1(e.target.value)}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        {/* Section 2 */}
                                        <h5 className="title mt-4">Section 2</h5>
                                        <Row>
                                            <Col md="12">
                                                <FormGroup>
                                                    <Label>Header 2</Label>
                                                    <Input
                                                        type="text"
                                                        placeholder="e.g., Release of Liability"
                                                        value={header2}
                                                        onChange={(e) => setHeader2(e.target.value)}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="12">
                                                <FormGroup>
                                                    <Label>Waiver Text 2</Label>
                                                    <Input
                                                        type="textarea"
                                                        rows="5"
                                                        value={waiverText2}
                                                        onChange={(e) => setWaiverText2(e.target.value)}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        {/* Section 3 */}
                                        <h5 className="title mt-4">Section 3</h5>
                                        <Row>
                                            <Col md="12">
                                                <FormGroup>
                                                    <Label>Header 3</Label>
                                                    <Input
                                                        type="text"
                                                        placeholder="e.g., Indemnification"
                                                        value={header3}
                                                        onChange={(e) => setHeader3(e.target.value)}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="12">
                                                <FormGroup>
                                                    <Label>Waiver Text 3</Label>
                                                    <Input
                                                        type="textarea"
                                                        rows="5"
                                                        value={waiverText3}
                                                        onChange={(e) => setWaiverText3(e.target.value)}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        {/* Image Upload */}
                                        <h5 className="title mt-4">Header Image (Optional)</h5>
                                        <Row>
                                            <Col md="12">
                                                <FormGroup>
                                                    <Label>Upload Image or Enter URL</Label>
                                                    <div className="mb-3">
                                                        <Button
                                                            color="info"
                                                            size="sm"
                                                            onClick={() => fileInputRef.current.click()}
                                                        >
                                                            <i className="tim-icons icon-upload"/> Choose File
                                                        </Button>
                                                        <input
                                                            ref={fileInputRef}
                                                            type="file"
                                                            accept="image/*"
                                                            style={{ display: 'none' }}
                                                            onChange={handleImageSelect}
                                                        />
                                                        {imageFile && (
                                                            <span className="ml-2 text-success">
                                                                {imageFile.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <InputGroup className={classnames({
                                                        "input-group-focus": imageUrlFocus
                                                    })}>
                                                        <InputGroupAddon addonType="prepend">
                                                            <InputGroupText>
                                                                <i className="tim-icons icon-image-02"/>
                                                            </InputGroupText>
                                                        </InputGroupAddon>
                                                        <Input
                                                            type="text"
                                                            placeholder="https://example.com/logo.png"
                                                            value={imageFile ? "Using uploaded file" : imageUrl}
                                                            onChange={(e) => !imageFile && setImageUrl(e.target.value)}
                                                            onFocus={() => setImageUrlFocus(true)}
                                                            onBlur={() => setImageUrlFocus(false)}
                                                            disabled={!!imageFile}
                                                        />
                                                    </InputGroup>
                                                    {imagePreview && (
                                                        <div className="mt-3">
                                                            <img
                                                                src={imagePreview}
                                                                alt="Preview"
                                                                style={{ maxWidth: '300px', maxHeight: '200px' }}
                                                            />
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        {/* Settings */}
                                        <Row>
                                            <Col md="12">
                                                <FormGroup check>
                                                    <Label check>
                                                        <Input
                                                            type="checkbox"
                                                            checked={enabled}
                                                            onChange={(e) => setEnabled(e.target.checked)}
                                                        />
                                                        <span className="form-check-sign"/>
                                                        Enable this waiver for public submission
                                                    </Label>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </form>
                                </CardBody>
                                <div className="card-footer">
                                    <Button color="info" onClick={handleSubmit}>
                                        <i className="tim-icons icon-check-2"/> Update Waiver
                                    </Button>
                                    <Button color="secondary" onClick={handleCancel}>
                                        <i className="tim-icons icon-simple-remove"/> Cancel
                                    </Button>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </>
        );
    } else {
        history.push("/admin/dashboard");
        return null;
    }
};

export default EditWaiver;
